const DEFAULT_MAX_STACK_SIZE = 50;

export class UndoRedoManager {
	private undoStack: string[] = [];
	private redoStack: string[] = [];
	private maxStackSize: number;

	constructor(maxStackSize: number = DEFAULT_MAX_STACK_SIZE) {
		this.maxStackSize = maxStackSize;
	}

	private trimStack(stack: string[]): void {
		while (stack.length > this.maxStackSize) {
			stack.shift();
		}
	}

	addToUndoStack(title: string): void {
		// Don't add duplicate consecutive states (compare by title)
		const lastState = this.undoStack[this.undoStack.length - 1];
		if (!lastState || lastState !== title) {
			this.undoStack.push(title);
			this.trimStack(this.undoStack);
		}
	}

	undo(currentTitle: string): string {
		if (this.undoStack.length === 0) {
			return currentTitle;
		}

		// Peek at the top of the stack - if it's the same title as current state, skip it
		while (
			this.undoStack.length > 0 &&
			this.undoStack[this.undoStack.length - 1] === currentTitle
		) {
			this.undoStack.pop();
		}

		if (this.undoStack.length === 0) {
			return currentTitle;
		}

		const previousState = this.undoStack.pop()!;
		// Only add to redo stack if the titles are different
		if (previousState !== currentTitle) {
			this.redoStack.push(currentTitle);
			this.trimStack(this.redoStack);
		}
		return previousState;
	}

	redo(currentTitle: string): string {
		if (this.redoStack.length > 0) {
			const nextState = this.redoStack.pop()!;
			// Only add to undo stack if the titles are different
			if (nextState !== currentTitle) {
				this.undoStack.push(currentTitle);
				this.trimStack(this.undoStack);
			}
			return nextState;
		}
		return currentTitle;
	}

	canUndo(): boolean {
		return this.undoStack.length > 0;
	}

	canRedo(): boolean {
		return this.redoStack.length > 0;
	}

	clear(): void {
		this.undoStack = [];
		this.redoStack = [];
	}
}
